// src/AuthForm.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { FiKey, FiUser } from "react-icons/fi";


function AuthForm() {
  const [view, setView] = useState("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { supabaseClient: supabase } = useSessionContext();
const [submitting, setSubmitting] = useState(false);


  // Fire Google Ads conversion (safe, with fallback timer)
 // ⬇️ exact send_to from your Google Ads conversion
const GOOGLE_ADS_SIGNUP_SEND_TO = "AW-17666172528/xjTqCJCqn7AbEPDM8edB";

// Fire GA4 + Google Ads conversion after successful signup
const fireSignupConversion = () => {
  if (typeof window === "undefined" || !window.gtag) return;

  // 1) GA4 (optional but nice to keep)
  window.gtag("event", "sign_up", { method: "email" });

  // 2) Google Ads conversion (this is the one Ads expects)
  let ack = false;
  window.gtag("event", "conversion", {
    send_to: GOOGLE_ADS_SIGNUP_SEND_TO,
    event_callback: () => {
      ack = true;
      if (process.env.NODE_ENV !== "production") console.debug("[Ads] conversion ack");
    },
  });

  // Small safety delay so the hit isn't dropped if navigation happens instantly
  setTimeout(() => {
    if (!ack && process.env.NODE_ENV !== "production") {
      console.debug("[Ads] fired (no ack)");
    }
  }, 400);
};

 const handleAuth = async (e) => {
  e.preventDefault();
  if (submitting) return;
  setSubmitting(true);
  setErrorMsg("");
  setMessage("");
  try {
    if (view === "sign-up") {
      if (!agreed) {
        setErrorMsg("⚠️ You must agree to the Terms & Conditions and Privacy Policy.");
        return;
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // This is fine; we still count the conversion when the sign-up API succeeds.
          emailRedirectTo: "https://www.vintariai.com/strategist",
        },
      });

      if (error) {
        setErrorMsg(error.message);
      } else {
        fireSignupConversion();               // <-- fire right after successful sign-up
        setMessage("✅ Check your email to confirm your registration.");
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setErrorMsg(error.message);
      else navigate("/strategist");
    }
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="w-full h-screen flex items-center justify-center bg-black text-white px-4">
      <form
        onSubmit={handleAuth}
        className="w-full max-w-md bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-8 space-y-6 shadow-xl transition-all"
      >
        <h2 className="text-2xl font-bold text-center">
          {view === "sign-up" ? "Create Account" : "Log In"}
        </h2>

        <input
          type="email"
          placeholder="Email"
          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-purple-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <input
          type="password"
          placeholder="Password"
          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-purple-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {view === "sign-up" && (
          <div className="flex items-start space-x-2 text-sm text-white/70">
            <input
              type="checkbox"
              id="agree"
              checked={agreed}
              onChange={() => setAgreed(!agreed)}
              className="mt-1 accent-purple-600"
            />
            <label htmlFor="agree">
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                Privacy Policy
              </a>.
            </label>
          </div>
        )}

        {errorMsg && <p className="text-red-400 text-sm text-center">{errorMsg}</p>}
        {message && <p className="text-green-400 text-sm text-center">{message}</p>}

<button
  type="submit"
  disabled={submitting}
  className={`w-full py-3 rounded-lg font-semibold transition ${
    submitting
      ? "bg-purple-500/60 cursor-not-allowed"
      : "bg-purple-600 hover:bg-purple-700"
  }`}
>
  {submitting ? "Please wait…" : (view === "sign-up" ? "Register" : "Log In")}
</button>



  <div className="text-center text-sm text-white/70 space-y-3">
  {view === "sign-up" ? (
    <p>
      Already have an account?{" "}
      <span
        onClick={() => setView("sign-in")}
        className="underline cursor-pointer hover:text-purple-300"
      >
        Log In
      </span>
    </p>
  ) : (
    <>
      <p>
        Don’t have an account?{" "}
        <span
          onClick={() => setView("sign-up")}
          className="underline cursor-pointer hover:text-purple-300"
        >
          Register
        </span>
      </p>

      {/* Action chips */}
      <div className="flex items-center justify-center gap-3">
        <a
          href="/forgot-password"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 bg-white/5
                     hover:bg-white/10 hover:border-white/25 transition"
        >
          <FiKey className="opacity-80" />
          <span className="hover:text-purple-300">Forgot password</span>
        </a>

        <a
          href="/forgot-username"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 bg-white/5
                     hover:bg-white/10 hover:border-white/25 transition"
        >
          <FiUser className="opacity-80" />
          <span className="hover:text-purple-300">Forgot username</span>
        </a>
      </div>
    </>
  )}
</div>



      </form>
    </div>
  );
}

export default AuthForm;
